/**
 * THEME MANAGER — Applica il brand del tenant (white-label opzione C).
 *
 * Deve stare DENTRO il Provider Redux. Flusso:
 *   1. quando l'utente è autenticato, scarica la palette dal backend
 *      (dispatch fetchTheme → state.theme.palette);
 *   2. la palette EFFETTIVA è: backend → cache (localStorage) → DEFAULT_PALETTE;
 *   3. la applica come CSS var (applyBrandPalette) e ricostruisce il theme MUI
 *      (createAppTheme), fornito via ThemeProvider;
 *   4. quando arriva dal backend la salva in cache (anti-flash al reload).
 *
 * La pre-applicazione dalla cache PRIMA del primo paint avviene in main.tsx.
 */

import { useEffect, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@mui/material/styles';
import { itIT, enUS } from '@mui/x-data-grid/locales';
import CssBaseline from '@mui/material/CssBaseline';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { applyBrandPalette, cachePalette, readCachedPalette, DEFAULT_PALETTE } from './brandPalette';
import { createAppTheme } from './theme';
import { fetchTheme } from './themeSlice';

export function ThemeManager({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const user = useAppSelector((s) => s.auth.user);
  const fetched = useAppSelector((s) => s.theme.palette);
  // Il backend ha già risposto? Distingue "non ancora caricata" da "non esiste".
  const loaded = useAppSelector((s) => s.theme.status) === 'succeeded';

  // Scarica la palette dal backend quando l'utente è autenticato.
  useEffect(() => {
    if (user) dispatch(fetchTheme());
  }, [user, dispatch]);

  // Quando arriva dal backend, aggiorna la cache anti-flash.
  useEffect(() => {
    if (fetched) cachePalette(fetched);
  }, [fetched]);

  // Palette effettiva: backend > cache > default. La cache vale finché il backend
  // non ha risposto — serve a non far sfarfallare il tema all'avvio — ma appena
  // la risposta arriva comanda lei: se dice che palette non ce n'è (company senza
  // colori, o palette appena cancellata da qualcun altro) si va ai predefiniti.
  // Continuare a leggere la cache in quel caso mostrerebbe per sempre i colori
  // cancellati a chi li aveva già visti.
  const palette = useMemo(
    () => fetched ?? (loaded ? DEFAULT_PALETTE : (readCachedPalette() ?? DEFAULT_PALETTE)),
    [fetched, loaded],
  );

  useEffect(() => {
    applyBrandPalette(palette);
  }, [palette]);

  // localeText del DataGrid in base alla lingua corrente (it/en): il theme si
  // ricostruisce al cambio lingua, ri-localizzando il footer di paginazione.
  const dataGridLocale = i18n.language.startsWith('it') ? itIT : enUS;
  const muiTheme = useMemo(() => createAppTheme(palette, dataGridLocale), [palette, dataGridLocale]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
